import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Cake,
  Users,
  Tent,
  Flower2,
  Sparkles,
  Heart,
  Sun,
  PackageCheck,
  ArrowRight,
  CheckCircle2,
} from 'lucide-react';

const HERO_IMG = 'https://images.unsplash.com/photo-1656615720849-db697d06d820';

const OCCASIONS = [
  {
    title: 'Birthdays',
    description: 'From kids’ parties to milestone celebrations, make every birthday special and waste-free.',
    image: 'https://images.unsplash.com/photo-1545696563-af8f6ec2295a',
    icon: <Cake size={28} />,
  },
  {
    title: 'Family Events',
    description: 'Sunday lunches, reunions and multi-generational get-togethers made effortless and memorable.',
    image: 'https://images.unsplash.com/photo-1627662166825-a860471632e4',
    icon: <Users size={28} />,
  },
  {
    title: 'Camping & Outdoor Activities',
    description: 'Lightweight, edible and compostable, the perfect companions for camping, hiking and weekend trips.',
    image: '/assets/images/camping.png',
    imageWebp: '/assets/images/camping.webp',
    icon: <Tent size={28} />,
  },
  {
    title: 'Backyard & Garden Gatherings',
    description: 'Garden parties, barbecues and picnics where you can simply enjoy, with no clean-up afterwards.',
    image: '/assets/images/backyard.jpg',
    icon: <Flower2 size={28} />,
  },
];

const BENEFITS = [
  {
    icon: <Sparkles size={28} />,
    title: 'Easy to Organize',
    description:
      'Skip the dishes and the recycling sorting. Edible cups and cutlery turn hosting into pure enjoyment.',
  },
  {
    icon: <Users size={28} />,
    title: 'Perfect for Small & Medium Groups',
    description:
      'Available in convenient pack sizes that fit family dinners, picnics and intimate celebrations.',
  },
  {
    icon: <Sun size={28} />,
    title: 'Indoor & Outdoor Friendly',
    description:
      'Use them at the kitchen table, in the garden, on a picnic blanket or around a campfire: they fit anywhere.',
  },
  {
    icon: <PackageCheck size={28} />,
    title: 'Convenient & User-Friendly',
    description:
      'No washing, no plastic waste, no leftover trash bags, just edible or fully compostable goodness.',
  },
  {
    icon: <Heart size={28} />,
    title: 'Memorable for Guests',
    description:
      'A simple but unforgettable detail that turns ordinary gatherings into stories worth retelling.',
  },
  {
    icon: <CheckCircle2 size={28} />,
    title: 'Kid & Family Safe',
    description:
      'Made from natural ingredients: vegan, GMO-free, with clear allergen labelling for peace of mind.',
  },
];

const GALLERY = [
  {
    src: '/assets/images/worth1.png',
    webp: '/assets/images/worth1.webp',
    alt: 'Cupffee edible cup at the beach with picnic basket and flowers',
  },
  {
    src: '/assets/images/worth2.png',
    webp: '/assets/images/worth2.webp',
    alt: 'Person in green blazer holding a Cupffee edible cup',
  },
  {
    src: '/assets/images/worth3.png',
    webp: '/assets/images/worth3.webp',
    alt: 'Cupffee cups served outdoors in an olive grove with pumpkins and lemons',
  },
];

const PrivateGatherings = () => {
  useEffect(() => {
    document.title = 'Private Gatherings | Where It’s Used | Magomeal';
  }, []);

  return (
    <div className="usecase-page private-gatherings-page">
      {/* Hero */}
      <section className="usecase-hero">
        <div className="container usecase-hero-container">
          <div className="usecase-hero-text">
            <span className="usecase-eyebrow">Where It’s Used</span>
            <h1 className="display-medium">Private Gatherings</h1>
            <p className="body-large">
              Create memorable moments together. Magomeal’s edible cups and cutlery are designed for
              relaxed, family-and-friends gatherings, perfect for birthdays, picnics and weekend
              celebrations, indoors or out.
            </p>
            <div className="usecase-hero-actions">
              <Link to="/contact" className="btn-primary">
                Plan Your Private Gathering
                <ArrowRight size={18} />
              </Link>
              <Link to="/shop" className="btn-secondary">
                Discover Solutions for Social Events
              </Link>
            </div>
          </div>
          <div className="usecase-hero-image-wrap">
            <img
              src={HERO_IMG}
              alt="Friends and family gathering outdoors"
              className="usecase-hero-image"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="usecase-intro">
        <div className="container">
          <div className="usecase-intro-inner">
            <p className="body-large">
              Whether you’re hosting a backyard birthday, packing a basket for a weekend picnic or
              gathering around a campfire with friends, Magomeal turns everyday get-togethers into something
              a little more special, and a lot more sustainable. Comfort, convenience and sharing,
              without the clean-up.
            </p>
          </div>
        </div>
      </section>

      {/* Occasion Types */}
      <section className="pg-occasions">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Occasion types</h2>
            <p className="body-large">Designed for the moments that matter most.</p>
          </div>
          <div className="pg-occasions-grid">
            {OCCASIONS.map((o) => (
              <article key={o.title} className="pg-occasion-card hover-lift">
                <div className="pg-occasion-image-wrap">
                  {o.imageWebp ? (
                    <picture>
                      <source srcSet={o.imageWebp} type="image/webp" />
                      <img src={o.image} alt={o.title} className="pg-occasion-image" loading="lazy" />
                    </picture>
                  ) : (
                    <img src={o.image} alt={o.title} className="pg-occasion-image" loading="lazy" />
                  )}
                  <div className="pg-occasion-icon">{o.icon}</div>
                </div>
                <div className="pg-occasion-body">
                  <h3 className="network-card-title">{o.title}</h3>
                  <p className="network-card-content">{o.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="usecase-benefits">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Why Magomeal for private gatherings</h2>
            <p className="body-large">Designed for relaxed social gatherings.</p>
          </div>
          <div className="usecase-benefits-grid">
            {BENEFITS.map((b, i) => (
              <div key={i} className="usecase-benefit-card hover-lift">
                <div className="usecase-benefit-icon">{b.icon}</div>
                <h3 className="network-card-title">{b.title}</h3>
                <p className="network-card-content">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="pg-gallery-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Moments worth sharing</h2>
            <p className="body-large">Casual social environments, outdoor leisure and togetherness.</p>
          </div>
          <div className="pg-gallery-grid">
            {GALLERY.map((g, i) => (
              <figure key={i} className={`pg-gallery-item pg-gallery-item-${i + 1}`}>
                {g.webp ? (
                  <picture>
                    <source srcSet={g.webp} type="image/webp" />
                    <img src={g.src} alt={g.alt} loading="lazy" />
                  </picture>
                ) : (
                  <img src={g.src} alt={g.alt} loading="lazy" />
                )}
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="usecase-cta">
        <div className="container">
          <div className="usecase-cta-card">
            <h2 className="heading-1">Plan your private gathering</h2>
            <p className="body-large">
              Tell us about your group size and occasion, we’ll help you pick the perfect mix
              of edible cups and cutlery so you can focus on the people, not the dishes.
            </p>
            <div className="usecase-cta-actions">
              <Link to="/contact" className="btn-primary">
                Get Started
                <ArrowRight size={18} />
              </Link>
              <Link to="/shop" className="btn-outline">
                Browse Products
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivateGatherings;
